import { jwtDecode } from 'jwt-decode'
import { usePathname } from 'next/navigation'
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'

import { REFRESH_TOKEN_KEY } from '@/legacy-src/constants/storage'
import { useApis } from '@/legacy-src/contexts/api-context'
import UserActions from '@/legacy-src/redux/UserRedux'
import { useAppSelector } from '@/legacy-src/store/hooks'
import type { ReduxState } from '@/legacy-src/store/reducers'
import { fetchingSelector } from '@/legacy-src/store/selectors/GeneralSelectors'
import type { UserType } from '@/legacy-src/types/UserStateType'

type SessionContextType = { user?: UserType; isLoading: boolean; isAuthenticating: boolean; isLoggedIn: boolean }

const SessionContext = createContext<SessionContextType>({} as SessionContextType)

const SessionProvider: React.FC<{ children: React.ReactNode }> = (props) => {
  const { api } = useApis()

  const pathname = usePathname()

  const [isAuthenticating, setIsAuthenticating] = useState(true)

  const user = useAppSelector((state) => state.user)
  const isLoading = useAppSelector((state: ReduxState) => fetchingSelector(state, 'user'))

  useEffect(() => {
    const doRefresh = async () => {
      const jwtToken = jwtDecode(refreshToken)
      if (jwtToken.exp > +new Date() / 1000) {
        await api.refresh?.(refreshToken, [
          UserActions.getUserDataRequest(
            null,
            true,
            {
              event: 'loginSuccessful',
            },
            undefined,
            undefined,
            pathname,
          ),
        ])
      } else {
        setIsAuthenticating(false)
      }
    }

    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
    // we are already doing the refresh in impersonate
    if (refreshToken && pathname && !pathname.startsWith('/impersonate/')) {
      doRefresh()
    } else {
      setIsAuthenticating(false)
    }
  }, [])

  const value = useMemo(
    () => ({ user, isLoading, isAuthenticating, isLoggedIn: !!user?.id }),
    [user, isLoading, isAuthenticating],
  )

  return <SessionContext.Provider value={value} {...props} />
}

const useSession = () => useContext(SessionContext)

export { SessionProvider, useSession }
